import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';
import {AuthenticationService} from '../services/authentication.service';
import {UtilsService} from '../services/utils.service';
import {CreditsRefreshService} from '../services/credits-refresh.service';

declare let $: any;

@Injectable()
export class AppInterceptor implements HttpInterceptor {
    // Router and ToastrService were injected only to redirect and toast on a
    // 401. Both are gone, so neither is needed here any more.
    constructor(private authenticationService: AuthenticationService,
                private utilsService: UtilsService,
                private creditsRefresh: CreditsRefreshService) {
    }

    /**
     * How many loader-visible requests are in flight.
     *
     * Only requests that opted in (no `skipLoader` param) are counted. It used to
     * be incremented under that condition but decremented unconditionally, so
     * every skipLoader request -- which the AI studio uses for nearly all of its
     * calls -- pushed the count negative and released the loader while genuine
     * requests were still running.
     */
    private inFlight = 0;

    /**
     * Delay before the loader appears, and the minimum it stays once it has.
     *
     * There used to be a single MIN_DISPLAY_MS of 3600, applied as a floor *after*
     * the response arrived: every request, however fast, held the loader on screen
     * for at least 3.6 seconds. That is an interaction cost on every page in the
     * app, not just the slow ones.
     *
     * The shape that actually avoids a flash of spinner is the other way round --
     * wait a moment before showing anything, so quick requests never show a loader
     * at all, and once it is up keep it briefly so it does not blink out. Anything
     * that resolves inside SHOW_DELAY_MS is now invisible rather than padded.
     */
    private readonly SHOW_DELAY_MS = 250;
    private readonly MIN_VISIBLE_MS = 400;

    private showTimer: any = null;
    private shownAt = 0;
    private visible = false;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        /*
         * Recorded per request: the same request must not be counted on the way
         * in and uncounted on the way out under different rules. That also makes
         * the route test safe — a request begun in the studio stays untracked
         * even if the reader navigates away before it answers.
         *
         * The route test covers what skipLoader misses. The studio passes that
         * param on nearly all of its calls, but "nearly" is the problem: one
         * call without it puts a full-screen sheet over the workspace.
         */
        const tracked = !req.params.get('skipLoader') && !this.utilsService.onPreloaderFreeRoute();

        if (tracked) {
            this.requestStarted();
        }

        let settled = false;
        const settle = () => {
            // http can emit more than one event; the loader is released once.
            if (tracked && !settled) {
                settled = true;
                this.requestFinished();
            }
        };

        /*
         * A POST to one of our own backends may have spent credits, so the
         * header's balance pill is told to re-read itself once the response
         * lands. See CreditsRefreshService.
         *
         * Decided here rather than at each call site: the AI studio alone POSTs
         * from fifteen places, and the one that eventually gets forgotten is the
         * one that leaves a stale number in the header. A GET never spends, and
         * a third-party host is none of our business.
         *
         * Matched on our api prefix, which covers indiapicture, exclusive and
         * budget alike. It fires for a few POSTs that spend nothing — an upload,
         * a profile save — and that costs one skipLoader GET, which is cheaper
         * than a rule that has to be kept in step with every new endpoint.
         */
        const mayHaveSpent = req.method === 'POST' && req.url.indexOf('/api/indiapicture') !== -1;

        return next.handle(req).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    settle();

                    if (mayHaveSpent && event.status >= 200 && event.status < 300) {
                        this.creditsRefresh.ping();
                    }
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                settle();

                if (error.status === 401) {
                    /*
                     * A 401 is handled quietly: no toast, no redirect.
                     *
                     * It used to toast 'Unauthorize, Please relogin' and navigate to
                     * '/'. That turns any single unauthorised call into a full
                     * page-level interruption, which is wrong now that the AI and
                     * profile endpoints require a token: pages like /ai are
                     * reachable while signed out and make API calls of their own, so
                     * a background 401 would have thrown the visitor back to the
                     * homepage mid-session.
                     *
                     * The error is still re-thrown below, so whichever component
                     * made the call decides what the user sees -- the chat page, for
                     * one, already has its own sign-in card for this.
                     */
                    const reason = error.error && error.error.error;

                    /*
                     * Credentials are cleared only when the token itself is dead.
                     *
                     * The original condition was `reason == 'Token is Expired' ||
                     * 'Token is Invalid'`, which is always true -- the second operand
                     * is a non-empty string literal, not a comparison -- so every
                     * 401 signed the user out, including 'no token provided' on a
                     * page they were browsing legitimately.
                     */
                    if (reason === 'Token is Expired' || reason === 'Token is Invalid') {
                        this.authenticationService.logout();
                    }
                }

                return throwError(error);
            }),
            /*
             * A request that is UNSUBSCRIBED never reaches map or catchError, so
             * without this it was never uncounted. inFlight stuck above zero, the
             * pending show-timer raised the sheet, and nothing ever lowered it.
             *
             * That is exactly what signing in did on production: the header's
             * accountDashboardData call (tracked, takeUntil(destroy$)) was still
             * in flight when the router moved to /ai, which has no header, so
             * the header's destroy$ cancelled it. Locally and on staging the
             * call answered before the navigation finished, so it never showed.
             */
            finalize(settle)
        );
    }

    private requestStarted(): void {
        this.inFlight++;

        if (this.visible || this.showTimer !== null) {
            return;
        }

        this.showTimer = setTimeout(() => {
            this.showTimer = null;

            // Nothing left to wait for by the time the delay elapsed: never show.
            // Asked again here, not only when the request began: a request
            // started on another page can outlive a navigation into the studio,
            // and the sheet must not land on top of it.
            if (this.inFlight > 0 && !this.utilsService.onPreloaderFreeRoute()) {
                this.visible = true;
                this.shownAt = Date.now();
                $('.preloader').stop(true, true).fadeIn('fast');
            }
        }, this.SHOW_DELAY_MS);
    }

    private requestFinished(): void {
        this.inFlight = Math.max(0, this.inFlight - 1);

        if (this.inFlight > 0) {
            return;
        }

        if (this.showTimer !== null) {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }

        if (!this.visible) {
            return;
        }

        const hide = () => {
            this.visible = false;
            $('.preloader').stop(true, true).fadeOut('fast');
        };

        const remaining = this.MIN_VISIBLE_MS - (Date.now() - this.shownAt);

        if (remaining > 0) {
            setTimeout(hide, remaining);
        } else {
            hide();
        }
    }
}
